import { useCallback, useEffect, useMemo, useState } from 'react'
import './FlashcardsDeck.css'
import { type CrucialReactProps } from './crucialReactMount'
import { setHostTheme, type ThemeMode } from './themeHandler'

export type Flashcard = {
  id: string
  front: string
  back: string
}

export type FlashcardsDeckConfig = CrucialReactProps & {
  cards: Flashcard[]
  frontLabel: string
  backLabel: string
  flipHint: string
  playLabel: string
  pauseLabel: string
  previousLabel: string
  nextLabel: string
  aiLabel: string
  fullscreenLabel: string
  exitFullscreenLabel: string
  aiPromptTemplate: string
  progressAriaLabel: string
  autoPlayMs?: number
}

type FlashcardsDeckProps = {
  config: FlashcardsDeckConfig
  theme?: ThemeMode
}

const formatTemplate = (template: string, values: Record<string, number | string>) =>
  template.replace(/\{(\w+)\}/g, (_, key: string) => String(values[key] ?? ''))

const getChatPrompt = (template: string, card: Flashcard) =>
  formatTemplate(template, { front: card.front, back: card.back })

export default function FlashcardsDeck({ config, theme }: FlashcardsDeckProps) {
  const { cards } = config
  const totalCards = cards.length
  const activeTheme = theme ?? 'dark'
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isFlipped, setIsFlipped] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)

  const currentCard = cards[currentIndex]
  const progress = totalCards ? (currentIndex / totalCards) * 100 : 0
  const progressLabel = formatTemplate(config.progressAriaLabel, {
    current: Math.min(currentIndex + 1, totalCards),
    total: totalCards,
  })

  useEffect(() => {
    setCurrentIndex(0)
    setIsFlipped(false)
    setIsPlaying(false)
    setIsFullscreen(false)
  }, [cards])

  const goToCard = useCallback(
    (delta: number) => {
      if (totalCards === 0) return
      setCurrentIndex((prev) => (prev + delta + totalCards) % totalCards)
      setIsFlipped(false)
    },
    [totalCards],
  )

  const flipCard = useCallback(() => {
    if (!currentCard) return
    setIsFlipped((prev) => !prev)
  }, [currentCard])

  const handleAsk = useCallback(() => {
    if (!currentCard) return
    const prompt = getChatPrompt(config.aiPromptTemplate, currentCard)
    const send = (window as Window & { sendAiChatMessageToOverlay?: (message: string) => void })
      .sendAiChatMessageToOverlay
    if (send) {
      send(prompt)
    }
  }, [config.aiPromptTemplate, currentCard])

  const handleThemeToggle = useCallback(() => {
    setHostTheme(activeTheme === 'dark' ? 'light' : 'dark')
  }, [activeTheme])

  const autoPlayMs = useMemo(() => Math.max(1500, config.autoPlayMs ?? 5000), [config.autoPlayMs])

  useEffect(() => {
    if (!isPlaying || totalCards === 0) return

    const intervalId = window.setInterval(() => {
      setIsFlipped((prev) => {
        if (!prev) return true
        setCurrentIndex((current) => (current + 1) % totalCards)
        return false
      })
    }, autoPlayMs)

    return () => window.clearInterval(intervalId)
  }, [autoPlayMs, isPlaying, totalCards])

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false)
        event.preventDefault()
        return
      }
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        flipCard()
        event.preventDefault()
      }
      if (event.key === 'ArrowLeft') {
        goToCard(-1)
        event.preventDefault()
      }
      if (event.key === 'ArrowRight') {
        goToCard(1)
        event.preventDefault()
      }
    }

    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [flipCard, goToCard, isFullscreen])

  useEffect(() => {
    if (!isFullscreen) return

    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = originalOverflow
    }
  }, [isFullscreen])

  if (!currentCard) return null

  const themeToggleLabel = activeTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'

  const deck = (
    <div className={`fc-deck font-study${isFullscreen ? ' is-fullscreen' : ''}`} data-theme={activeTheme}>
      <div className="fc-stage">
        {isFullscreen ? (
          <div className="fc-top-bar">
            <cer-btn
              type="button"
              className="fc-icon-button fc-fullscreen-close"
              onClick={() => setIsFullscreen(false)}
              title={config.exitFullscreenLabel}
              aria-label={config.exitFullscreenLabel}
            >
              <i className="fa-solid fa-xmark" />
            </cer-btn>
          </div>
        ) : null}

        <cer-btn type="button" className="fc-card" onClick={flipCard} aria-pressed={isFlipped}>
          <div className={`fc-card-face${isFlipped ? ' is-hidden' : ''}`}>
            <div className="fc-face-label">{config.frontLabel}</div>
            <div className="fc-face-content">{currentCard.front}</div>
            <div className="fc-face-hint">
              {currentIndex === 0 && !isFlipped ? config.flipHint : ''}
            </div>
          </div>
          <div className={`fc-card-face${isFlipped ? '' : ' is-hidden'}`}>
            <div className="fc-face-label">{config.backLabel}</div>
            <div className="fc-face-content">{currentCard.back}</div>
            <div className="fc-face-hint" />
          </div>
        </cer-btn>

        <div className="fc-progress" role="img" aria-label={progressLabel}>
          <span style={{ width: `${progress}%` }} />
        </div>

        <div className="fc-controls">
          <div className="fc-control-group fc-control-group--left fc-control-group--auto">
            <cer-btn
              type="button"
              className={`fc-icon-button fc-auto-toggle${isPlaying ? ' is-hidden' : ''}`}
              onClick={() => setIsPlaying(true)}
              title={config.playLabel}
              aria-label={config.playLabel}
            >
              <i className="fa-solid fa-play fa-fw" />
            </cer-btn>
            <cer-btn
              type="button"
              className={`fc-icon-button fc-auto-toggle${isPlaying ? '' : ' is-hidden'}`}
              onClick={() => setIsPlaying(false)}
              title={config.pauseLabel}
              aria-label={config.pauseLabel}
            >
              <i className="fa-solid fa-pause fa-fw" />
            </cer-btn>
            <cer-btn
              type="button"
              className="fc-icon-button fc-fullscreen-button"
              onClick={handleThemeToggle}
              title={themeToggleLabel}
              aria-label={themeToggleLabel}
            >
              <i
                className={
                  activeTheme === 'dark' ? 'fa-duotone fa-sun-bright fa-fw' : 'fa-duotone fa-moon-stars fa-fw'
                }
              />
            </cer-btn>
            <cer-btn
              type="button"
              className="fc-icon-button fc-fullscreen-button"
              onClick={() => setIsFullscreen((prev) => !prev)}
              title={isFullscreen ? config.exitFullscreenLabel : config.fullscreenLabel}
              aria-label={isFullscreen ? config.exitFullscreenLabel : config.fullscreenLabel}
            >
              <i
                className={
                  isFullscreen
                    ? 'fa-solid fa-down-left-and-up-right-to-center'
                    : 'fa-solid fa-up-right-and-down-left-from-center'
                }
              />
            </cer-btn>
          </div>

          <div className="fc-control-group fc-control-group--center">
            <cer-btn
              type="button"
              className="fc-icon-button"
              onClick={() => goToCard(-1)}
              title={config.previousLabel}
              aria-label={config.previousLabel}
            >
              <i className="fa-regular fa-arrow-left" />
            </cer-btn>
            <div className="fc-count">
              <span>{Math.min(currentIndex + 1, totalCards)}</span>/<span>{totalCards}</span>
            </div>
            <cer-btn
              type="button"
              className="fc-icon-button"
              onClick={() => goToCard(1)}
              title={config.nextLabel}
              aria-label={config.nextLabel}
            >
              <i className="fa-regular fa-arrow-right" />
            </cer-btn>
          </div>

          <div className="fc-control-group fc-control-group--right">
            <cer-btn
              type="button"
              className="fc-icon-button"
              onClick={handleAsk}
              title={config.aiLabel}
              aria-label={config.aiLabel}
            >
              <i className="fa-regular fa-sparkles" />
            </cer-btn>
          </div>
        </div>
      </div>
    </div>
  )

  if (!isFullscreen) {
    return deck
  }

  return (
    <div className="fc-fullscreen-overlay" data-theme={activeTheme} role="presentation">
      {deck}
    </div>
  )
}
